'use client';
import type { ReactElement } from 'react';
import { useAppSelector } from 'lib/hooks';
import { RootState } from 'lib/store';
import Link from 'next/link';
import { selectUserDynamicValues } from 'slices/dynamicDataSelectors';
import { UserDynamicValues } from 'types/user';
import { contentWithData } from 'utils/content';

export interface AccordionProps {
  categoryTag: string;
  externalCtaText: string;
  externalCtaUrl: string;
  headline: string;
  index: number;
  isExpanded: boolean;
  libraryPageSlug: string;
  summary: string;
  toggleExpanded: (index: null | number) => void;
}

export interface FormattedAccordion {
  categoryTag: string;
  externalCtaText: string;
  externalCtaUrl: string;
  filterKey: string;
  headline: string;
  libraryPageSlug: string;
  summary: string;
}

export default function Accordion({
  externalCtaText,
  externalCtaUrl,
  headline,
  index,
  isExpanded,
  libraryPageSlug,
  summary,
  toggleExpanded,
}: AccordionProps): ReactElement {
  const userData = useAppSelector((state: RootState) =>
    selectUserDynamicValues(state)
  ) as UserDynamicValues;

  const expandedClassName = isExpanded ? 'content-expanded' : 'content-closed';

  return (
    <div className={`accordion ${isExpanded ? 'open' : 'closed'}`}>
      <span>
        <h3 data-testid={`accordion-heading-${index}`}>
          {contentWithData(headline, userData)}
        </h3>
        <span>
          <button
            aria-expanded={isExpanded}
            aria-label="expand accordion button"
            className={expandedClassName}
            data-testid={`expand-accordion-button-${index}`}
            onClick={() => toggleExpanded(isExpanded ? null : index)}
          />
        </span>
      </span>

      <div className={`${isExpanded ? 'content-open' : 'content-closed'}`}>
        {contentWithData(summary, userData)}
        {externalCtaUrl && (
          <Link
            className="btn btn-secondary"
            href={externalCtaUrl}
            role="button"
            target="_blank"
          >
            {contentWithData(externalCtaText, userData)}
          </Link>
        )}
        {libraryPageSlug && (
          <Link href={`/${libraryPageSlug}`}>
            <button className="btn btn-secondary">Read more</button>
          </Link>
        )}
      </div>
    </div>
  );
}
